import { ERROR_MESSAGES } from 'constants/messages';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import { SubmissionErrors, hydraSchemaAnalyzer } from '@api-platform/admin';
import { yupResolver } from '@hookform/resolvers/yup';
import { createGatewaysBulk } from 'config/requests/gateways';
import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import isEmpty from 'lodash/isEmpty';
import { GatewaysFormSkeleton } from 'pages/Gateways/components/GatewaysFormSkeleton';
import { gatewayBulkSchema, gatewaySchema } from 'pages/Gateways/schemas/schema';
import { Form, HttpError, useCreate, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CreatePage } from 'shared/react-admin/Pages/CreatePage/CreatePage';
import { getServerErrorMessage } from 'utils/getServerErrorMessage';

import { GatewaysBulkCreateForm } from './GatewaysBulkCreateForm';
import { GatewaysCreateForm } from './GatewaysCreateForm';
import { GatewaysCreateModeToggle } from './GatewaysCreateModeToggle';
import { useGatewayCreateDefaultValues } from './hooks/useGatewayCreateDefaultValues';
import { GatewayCreateFormValues } from './types';
import { getGatewayBulkSubmissionErrors } from './utils/getGatewayBulkSubmissionErrors';
import { prepareGatewayBulkCreateSubmitValues } from './utils/prepareGatewayBulkCreateSubmitValues';
import { prepareGatewayCreateSubmitValues } from './utils/prepareGatewayCreateSubmitValues';

export const GatewaysCreate: React.FC = () => {
  const { toRoot } = useNavigateToRoot();
  const notify = useNotify();
  const [create] = useCreate();
  const [isBulk, setIsBulk] = useState(false);

  const { formDefaultValues, isLoading } = useGatewayCreateDefaultValues();

  async function onSubmit(formValues: FieldValues): Promise<SubmissionErrors | void> {
    try {
      await create(
        Resources.Gateways,
        { data: prepareGatewayCreateSubmitValues(formValues as GatewayCreateFormValues) },
        { returnPromise: true },
      );
      notify('Шлюз успешно создан!', { type: 'success' });
      toRoot();
    } catch (error) {
      notify(ERROR_MESSAGES.INVALID_FORM, { type: 'error' });
      return hydraSchemaAnalyzer().getSubmissionErrors(error as HttpError) || undefined;
    }
  }

  async function onBulkSubmit(formValues: FieldValues): Promise<SubmissionErrors | void> {
    try {
      await createGatewaysBulk(
        prepareGatewayBulkCreateSubmitValues(formValues as GatewayCreateFormValues),
      );
      notify('Шлюзы успешно созданы!', { type: 'success' });
      toRoot();
    } catch (error) {
      const submissionErrors = getGatewayBulkSubmissionErrors(error as HttpError);

      if (!isEmpty(submissionErrors)) {
        notify(ERROR_MESSAGES.INVALID_FORM, { type: 'error' });
        return submissionErrors;
      }

      notify(`Ошибка: ${await getServerErrorMessage(error)}`, { type: 'error' });
    }
  }

  return (
    <CreatePage listName="Шлюзы" title="Создание шлюза">
      {isLoading ? (
        <GatewaysFormSkeleton />
      ) : (
        <Form
          onSubmit={isBulk ? onBulkSubmit : onSubmit}
          record={formDefaultValues}
          resolver={yupResolver<FieldValues>(isBulk ? gatewayBulkSchema : gatewaySchema)}
        >
          <GatewaysCreateModeToggle isBulk={isBulk} onChange={setIsBulk} />
          {isBulk ? <GatewaysBulkCreateForm /> : <GatewaysCreateForm />}
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton key="cancel-button" linkType="back" variant="outlined" />,
              <SaveButton key="save-button" />,
            ]}
            sx={{ marginTop: 'auto', padding: 0 }}
          />
        </Form>
      )}
    </CreatePage>
  );
};
